import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent,
  type WheelEvent,
} from "react";
import { ContextMenu, type ContextMenuSection } from "../components/ContextMenu";
import { Icon } from "../components/Icon";
import { analyzeCableDraft } from "../domain/cable-compatibility";
import { instrumentBundleForDocument } from "../instruments/catalog";
import { useEditorStore, type Cable, type CableEndpoint } from "../app/store";
import { routeCable, type CableAnchor, type Point } from "./cable-routing";
import { InstrumentPanel, type PortPatchGuidance } from "./InstrumentPanel";
import type {
  PixiCableVisual,
  PixiExternalNodeVisual,
  PixiInstrumentRendererHandle,
  PixiLogicalRouteVisual,
  PixiRendererBackend,
  PixiSignalNodeVisual,
  PixiUtilityNodeVisual,
} from "./PixiInstrumentRenderer";
import { getNormalledRouteStates, traceSignal, validateSignalPatch } from "../domain/signal-graph";
import type { PortDefinition } from "../domain/instrument";
import { cableEndpoints, type ExternalEndpointNode, type UtilityNode } from "../domain/patch";
import {
  EXTERNAL_CARD_HEIGHT,
  EXTERNAL_CARD_WIDTH,
  endpointForPortId,
  externalEndpoints,
  utilityCardHeight,
  utilityCardWidth,
  utilityNodes,
  utilityPortId,
  workspaceDefinition,
  workspaceWorldHeight,
} from "./workspace-ports";
import { cachedCompiledInstrumentRuntime } from "./compiled-instrument";

const PixiInstrumentRenderer = lazy(async () => {
  const module = await import("./PixiInstrumentRenderer");
  return { default: module.PixiInstrumentRenderer };
});

type Camera = { x: number; y: number; scale: number };

function cameraTransform(camera: Camera) {
  return `translate(${camera.x}px, ${camera.y}px) scale(${camera.scale})`;
}
type CableEndpointRef = "endpointA" | "endpointB" | `branch:${number}`;
type EndpointPreview = { endpoint: CableEndpointRef; point: Point };
type CableEndpointEdit = { cableId: string; endpoint: CableEndpointRef };
type LooseEndDrag = {
  cableId: string;
  endpoint: CableEndpointRef;
  offset: Point;
  pointerId: number;
  point: Point;
};

type ExternalNodeDrag = {
  endpointId: string;
  offset: Point;
  pointerId: number;
  point: Point;
  start: Point;
  moved: boolean;
};

type UtilityNodeDrag = ExternalNodeDrag;
type JunctionDrag = {
  cableId: string;
  offset: Point;
  pointerId: number;
  point: Point;
};

type Bounds = { x: number; y: number; width: number; height: number };

const MIN_SCALE = 0.28;
const MAX_SCALE = 4;

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}
function portEndpoint(portId: string): CableEndpoint {
  return endpointForPortId(portId);
}

function branchIndex(endpoint: CableEndpointRef) {
  if (!endpoint.startsWith("branch:")) return null;
  const index = Number(endpoint.slice(7));
  return Number.isInteger(index) && index >= 0 ? index : null;
}

function cableEndpointAt(cable: Cable, endpoint: CableEndpointRef): CableEndpoint | null {
  if (endpoint === "endpointA" || endpoint === "endpointB") return cable[endpoint];
  const index = branchIndex(endpoint);
  return index === null ? null : (cable.branches?.[index] ?? null);
}

function cableEndpointUpdate(cable: Cable, endpoint: CableEndpointRef, value: CableEndpoint) {
  if (endpoint === "endpointA") return { endpointA: value };
  if (endpoint === "endpointB") return { endpointB: value };
  const index = branchIndex(endpoint);
  if (index === null) return {};
  return {
    branches: (cable.branches ?? []).map((candidate, candidateIndex) =>
      candidateIndex === index ? value : candidate,
    ),
  };
}

function cableEndpointName(endpoint: CableEndpointRef) {
  if (endpoint === "endpointA") return "end A";
  if (endpoint === "endpointB") return "end B";
  return `branch ${Number(endpoint.slice(7)) + 2}`;
}

function fixedEndpointFor(cable: Cable, endpoint: CableEndpointRef) {
  return endpoint === "endpointA" ? cable.endpointB : cable.endpointA;
}

function buildPixiCableVisual({
  alpha,
  cable,
  endpointPreview,
  junctionPreview,
  resolveEndpoint,
  selected,
  traced,
}: {
  alpha: number;
  cable: Cable;
  endpointPreview?: EndpointPreview;
  junctionPreview?: Point;
  resolveEndpoint: (endpoint: CableEndpoint) => CableAnchor | null;
  selected: boolean;
  traced: boolean;
}): PixiCableVisual | null {
  const endpointVisual = (
    endpoint: CableEndpoint,
    point: Point,
    loose: boolean,
    direction: Point | undefined,
  ) => ({
    direction: direction ?? { x: 0, y: 1 },
    loose,
    point,
    stackable: endpoint.kind === "port" && endpoint.plugStyle === "stackable",
  });

  if (cable.branches?.length && cable.junction) {
    const endpoints = cableEndpoints(cable);
    const endpointRefs = endpoints.map((_, index): CableEndpointRef =>
      index === 0 ? "endpointA" : index === 1 ? "endpointB" : `branch:${index - 2}`,
    );
    const anchors = endpoints.map((endpoint, index) =>
      endpointPreview?.endpoint === endpointRefs[index]
        ? ({ ...endpointPreview.point, surface: "loose" } satisfies CableAnchor)
        : resolveEndpoint(endpoint),
    );
    if (anchors.some((anchor) => !anchor)) return null;
    const resolvedAnchors = anchors as CableAnchor[];
    const junction = junctionPreview ?? cable.junction;
    const junctionAnchor: CableAnchor = { ...junction, surface: "loose" };
    const routes = resolvedAnchors.map((anchor, index) =>
      routeCable(junctionAnchor, anchor, cable.number + index),
    );

    return {
      alpha,
      colour: cable.colour,
      endpoints: endpoints.map((endpoint, index) =>
        endpointVisual(
          endpoint,
          resolvedAnchors[index]!,
          endpoint.kind === "loose" || endpointPreview?.endpoint === endpointRefs[index],
          routes[index]?.looseDirectionB,
        ),
      ),
      id: cable.id,
      junction,
      number: cable.number,
      prepared: Boolean(cable.prepared),
      segments: routes.map(({ geometryPoints }) => geometryPoints),
      selected,
      traced,
    };
  }

  const previewAnchor: CableAnchor | null = endpointPreview
    ? { ...endpointPreview.point, surface: "loose" }
    : null;
  const a =
    endpointPreview?.endpoint === "endpointA" && previewAnchor
      ? previewAnchor
      : resolveEndpoint(cable.endpointA);
  const b =
    endpointPreview?.endpoint === "endpointB" && previewAnchor
      ? previewAnchor
      : resolveEndpoint(cable.endpointB);
  if (!a || !b) return null;
  const route = routeCable(a, b, cable.number);
  const aIsLoose = cable.endpointA.kind === "loose" || endpointPreview?.endpoint === "endpointA";
  const bIsLoose = cable.endpointB.kind === "loose" || endpointPreview?.endpoint === "endpointB";

  return {
    alpha,
    colour: cable.colour,
    endpoints: [
      endpointVisual(cable.endpointA, a, aIsLoose, route.looseDirectionA),
      endpointVisual(cable.endpointB, b, bIsLoose, route.looseDirectionB),
    ],
    id: cable.id,
    labelPoint: route.labelPoint,
    number: cable.number,
    prepared: Boolean(cable.prepared),
    segments: [route.geometryPoints],
    selected,
    traced,
  };
}

function CablePlug({
  point,
  colour,
  actionLabel,
  onActivate,
  stackable = false,
}: {
  point: Point;
  colour: string;
  actionLabel?: string;
  onActivate?: () => void;
  stackable?: boolean;
}) {
  void colour;
  void stackable;
  if (!onActivate) return null;
  return (
    <g
      aria-label={actionLabel}
      className="cable-plug is-editable"
      onClick={(event) => {
        event.stopPropagation();
        onActivate();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        event.stopPropagation();
        onActivate();
      }}
      role="button"
      tabIndex={0}
      transform={`translate(${point.x} ${point.y})`}
    >
      {actionLabel && <title>{actionLabel}</title>}
      <circle className="plug-edit-hit" r="18" />
    </g>
  );
}

function LooseCablePlug({
  point,
  colour,
  direction,
  actionLabel,
  onNudge,
  onPointerDown,
}: {
  point: Point;
  colour: string;
  direction: Point;
  actionLabel?: string;
  onNudge?: (dx: number, dy: number) => void;
  onPointerDown?: (event: PointerEvent<SVGGElement>) => void;
}) {
  void colour;
  if (!onPointerDown && !onNudge) return null;
  const angle = (Math.atan2(direction.y, direction.x) * 180) / Math.PI;
  return (
    <g
      aria-label={actionLabel}
      className="loose-cable-plug is-editable"
      data-connector="3.5mm-ts"
      onKeyDown={(event) => {
        if (!onNudge) return;
        const step = event.shiftKey ? 1 : 5;
        const offset =
          event.key === "ArrowLeft"
            ? [-step, 0]
            : event.key === "ArrowRight"
              ? [step, 0]
              : event.key === "ArrowUp"
                ? [0, -step]
                : event.key === "ArrowDown"
                  ? [0, step]
                  : null;
        if (!offset) return;
        event.preventDefault();
        onNudge(offset[0], offset[1]);
      }}
      onPointerDown={onPointerDown}
      role="button"
      tabIndex={0}
      transform={`translate(${point.x} ${point.y}) rotate(${angle})`}
    >
      {actionLabel && <title>{actionLabel}</title>}
      <rect className="loose-plug-hit" height="48" rx="24" width="70" x="-8" y="-24" />
    </g>
  );
}

function BranchedCablePath({
  cable,
  dimmed,
  endpointPreview,
  junctionPreview,
  onEndpointActivate,
  onJunctionNudge,
  onJunctionPointerDown,
  onLooseEndNudge,
  onLooseEndPointerDown,
  onSelect,
  reconnecting,
  resolveEndpoint,
  selected,
  traced,
}: {
  cable: Cable;
  dimmed: boolean;
  endpointPreview?: EndpointPreview;
  junctionPreview?: Point;
  onEndpointActivate: (endpoint: CableEndpointRef) => void;
  onJunctionNudge: (dx: number, dy: number) => void;
  onJunctionPointerDown: (event: PointerEvent<SVGGElement>) => void;
  onLooseEndNudge: (endpoint: CableEndpointRef, dx: number, dy: number) => void;
  onLooseEndPointerDown: (endpoint: CableEndpointRef, event: PointerEvent<SVGGElement>) => void;
  onSelect: () => void;
  reconnecting: boolean;
  resolveEndpoint: (endpoint: CableEndpoint) => CableAnchor | null;
  selected: boolean;
  traced: boolean;
}) {
  if (!cable.junction) return null;
  const endpoints = cableEndpoints(cable);
  const endpointRefs = endpoints.map((_, index): CableEndpointRef =>
    index === 0 ? "endpointA" : index === 1 ? "endpointB" : `branch:${index - 2}`,
  );
  const anchors = endpoints.map((endpoint, index) =>
    endpointPreview?.endpoint === endpointRefs[index]
      ? ({ ...endpointPreview.point, surface: "loose" } satisfies CableAnchor)
      : resolveEndpoint(endpoint),
  );
  if (anchors.some((anchor) => !anchor)) return null;
  const junctionPoint = junctionPreview ?? cable.junction;
  const junctionAnchor: CableAnchor = { ...junctionPoint, surface: "loose" };
  const routes = anchors.map((anchor, index) =>
    routeCable(junctionAnchor, anchor!, cable.number + index),
  );
  return (
    <g
      className={`patch-cable is-branched${cable.prepared ? " is-prepared" : ""}${
        selected ? " is-selected" : ""
      }${reconnecting ? " is-reconnecting" : ""}${traced ? " is-traced" : ""}${
        dimmed ? " is-dimmed" : ""
      }`}
      data-cable-id={cable.id}
      onClick={(event) => {
        event.stopPropagation();
        onSelect();
      }}
    >
      {routes.map((route, index) => (
        <g className="cable-branch-segment" key={index}>
          <path
            aria-label={`Cable ${cable.number}, segment ${index + 1}`}
            className="cable-hit"
            d={route.path}
            onClick={(event) => {
              event.stopPropagation();
              onSelect();
            }}
            role="button"
            stroke="transparent"
            tabIndex={0}
          />
        </g>
      ))}
      {endpoints.map((endpoint, index) => {
        const point = anchors[index]!;
        const endpointRef = endpointRefs[index]!;
        const loose = endpoint.kind === "loose" || endpointPreview?.endpoint === endpointRef;
        const name = cableEndpointName(endpointRef);
        return loose ? (
          <LooseCablePlug
            actionLabel={selected ? `Move ${name} of Cable ${cable.number}` : undefined}
            colour={cable.colour}
            direction={routes[index]?.looseDirectionB ?? { x: 0, y: 1 }}
            key={endpointRef}
            onNudge={
              selected && endpoint.kind === "loose"
                ? (dx, dy) => onLooseEndNudge(endpointRef, dx, dy)
                : undefined
            }
            onPointerDown={
              selected && endpoint.kind === "loose"
                ? (event) => onLooseEndPointerDown(endpointRef, event)
                : undefined
            }
            point={point}
          />
        ) : (
          <CablePlug
            actionLabel={selected ? `Reconnect Cable ${cable.number} ${name}` : undefined}
            colour={cable.colour}
            key={endpointRef}
            onActivate={selected ? () => onEndpointActivate(endpointRef) : undefined}
            point={point}
            stackable={endpoint.plugStyle === "stackable"}
          />
        );
      })}
      <g
        aria-label={`Move Cable ${cable.number} junction`}
        className="cable-junction"
        onClick={(event) => {
          event.stopPropagation();
          onSelect();
        }}
        onPointerDown={onJunctionPointerDown}
        onKeyDown={(event) => {
          const step = event.shiftKey ? 1 : 8;
          const delta =
            event.key === "ArrowLeft"
              ? [-step, 0]
              : event.key === "ArrowRight"
                ? [step, 0]
                : event.key === "ArrowUp"
                  ? [0, -step]
                  : event.key === "ArrowDown"
                    ? [0, step]
                    : null;
          if (!delta) return;
          event.preventDefault();
          event.stopPropagation();
          onJunctionNudge(delta[0], delta[1]);
        }}
        role="button"
        tabIndex={0}
        transform={`translate(${junctionPoint.x} ${junctionPoint.y})`}
      >
        <circle className="cable-junction-outer" r="15" />
      </g>
    </g>
  );
}

function CablePath({
  cable,
  resolveEndpoint,
  selected,
  traced,
  dimmed,
  onSelect,
  endpointPreview,
  reconnecting,
  onEndpointActivate,
  onLooseEndPointerDown,
  onLooseEndNudge,
  junctionPreview,
  onJunctionNudge,
  onJunctionPointerDown,
}: {
  cable: Cable;
  resolveEndpoint: (endpoint: CableEndpoint) => CableAnchor | null;
  selected: boolean;
  traced: boolean;
  dimmed: boolean;
  onSelect: () => void;
  reconnecting: boolean;
  endpointPreview?: EndpointPreview;
  onEndpointActivate: (endpoint: CableEndpointRef) => void;
  onLooseEndPointerDown: (endpoint: CableEndpointRef, event: PointerEvent<SVGGElement>) => void;
  onLooseEndNudge: (endpoint: CableEndpointRef, dx: number, dy: number) => void;
  junctionPreview?: Point;
  onJunctionNudge: (dx: number, dy: number) => void;
  onJunctionPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  if (cable.branches?.length && cable.junction) {
    return (
      <BranchedCablePath
        cable={cable}
        dimmed={dimmed}
        endpointPreview={endpointPreview}
        junctionPreview={junctionPreview}
        onEndpointActivate={onEndpointActivate}
        onJunctionNudge={onJunctionNudge}
        onJunctionPointerDown={onJunctionPointerDown}
        onLooseEndNudge={onLooseEndNudge}
        onLooseEndPointerDown={onLooseEndPointerDown}
        onSelect={onSelect}
        reconnecting={reconnecting}
        resolveEndpoint={resolveEndpoint}
        selected={selected}
        traced={traced}
      />
    );
  }
  const previewAnchor: CableAnchor | null = endpointPreview
    ? { ...endpointPreview.point, surface: "loose" }
    : null;
  const a =
    endpointPreview?.endpoint === "endpointA" && previewAnchor
      ? previewAnchor
      : resolveEndpoint(cable.endpointA);
  const b =
    endpointPreview?.endpoint === "endpointB" && previewAnchor
      ? previewAnchor
      : resolveEndpoint(cable.endpointB);
  if (!a || !b) return null;

  const route = routeCable(a, b, cable.number);
  const aIsLoose = cable.endpointA.kind === "loose" || endpointPreview?.endpoint === "endpointA";
  const bIsLoose = cable.endpointB.kind === "loose" || endpointPreview?.endpoint === "endpointB";
  return (
    <g
      className={`patch-cable${cable.prepared ? " is-prepared" : ""}${
        selected ? " is-selected" : ""
      }${reconnecting ? " is-reconnecting" : ""}${traced ? " is-traced" : ""}${
        dimmed ? " is-dimmed" : ""
      }`}
      data-cable-id={cable.id}
      data-route-kind={route.kind}
      onClick={(event) => {
        event.stopPropagation();
        onSelect();
      }}
    >
      <path
        aria-label={`${cable.prepared ? "Prepared c" : "C"}able ${cable.number}`}
        className="cable-hit"
        d={route.path}
        onClick={(event) => {
          event.stopPropagation();
          onSelect();
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          event.stopPropagation();
          onSelect();
        }}
        role="button"
        stroke="transparent"
        tabIndex={0}
      />
      {aIsLoose ? (
        <LooseCablePlug
          actionLabel={selected ? `Move loose end A of Cable ${cable.number}` : undefined}
          colour={cable.colour}
          direction={route.looseDirectionA ?? { x: 0, y: 1 }}
          onNudge={
            selected && cable.endpointA.kind === "loose"
              ? (dx, dy) => onLooseEndNudge("endpointA", dx, dy)
              : undefined
          }
          onPointerDown={
            selected && cable.endpointA.kind === "loose"
              ? (event) => onLooseEndPointerDown("endpointA", event)
              : undefined
          }
          point={a}
        />
      ) : (
        <CablePlug
          actionLabel={selected ? `Reconnect Cable ${cable.number} end A` : undefined}
          colour={cable.colour}
          onActivate={selected ? () => onEndpointActivate("endpointA") : undefined}
          point={a}
          stackable={cable.endpointA.kind === "port" && cable.endpointA.plugStyle === "stackable"}
        />
      )}
      {bIsLoose ? (
        <LooseCablePlug
          actionLabel={selected ? `Move loose end B of Cable ${cable.number}` : undefined}
          colour={cable.colour}
          direction={route.looseDirectionB ?? { x: 0, y: 1 }}
          onNudge={
            selected && cable.endpointB.kind === "loose"
              ? (dx, dy) => onLooseEndNudge("endpointB", dx, dy)
              : undefined
          }
          onPointerDown={
            selected && cable.endpointB.kind === "loose"
              ? (event) => onLooseEndPointerDown("endpointB", event)
              : undefined
          }
          point={b}
        />
      ) : (
        <CablePlug
          actionLabel={selected ? `Reconnect Cable ${cable.number} end B` : undefined}
          colour={cable.colour}
          onActivate={selected ? () => onEndpointActivate("endpointB") : undefined}
          point={b}
          stackable={cable.endpointB.kind === "port" && cable.endpointB.plugStyle === "stackable"}
        />
      )}
    </g>
  );
}

function logicalRoutePath(from: Point, to: Point, index: number) {
  const lowest = Math.max(from.y, to.y);
  const rearRoute = lowest > 600;
  const lane = rearRoute ? 610 + (index % 4) * 13 : Math.min(545, lowest + 42 + (index % 5) * 12);
  return {
    path: `M ${from.x} ${from.y} C ${from.x} ${lane}, ${to.x} ${lane}, ${to.x} ${to.y}`,
    midpoint: { x: (from.x + to.x) / 2, y: lane },
  };
}

function LogicalRoute({
  id,
  label,
  from,
  to,
  index,
  signalType,
  kind,
  broken = false,
  selected,
  traced,
  dimmed,
  onSelect,
}: {
  id: string;
  label: string;
  from: Point;
  to: Point;
  index: number;
  signalType: PortDefinition["signalType"];
  kind: "normalled" | "internal";
  broken?: boolean;
  selected: boolean;
  traced: boolean;
  dimmed: boolean;
  onSelect: () => void;
}) {
  const route = logicalRoutePath(from, to, index);
  return (
    <g
      className={`logical-route is-${kind} signal-${signalType}${broken ? " is-broken" : " is-active"}${selected ? " is-selected" : ""}${traced ? " is-traced" : ""}${dimmed ? " is-dimmed" : ""}`}
      data-route-id={id}
    >
      <path
        aria-label={`${label}, ${broken ? "broken" : kind === "normalled" ? "active normal" : "internal signal path"}`}
        className="logical-route-hit"
        d={route.path}
        onClick={(event) => {
          event.stopPropagation();
          onSelect();
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          event.stopPropagation();
          onSelect();
        }}
        role="button"
        tabIndex={0}
      />
    </g>
  );
}

function SemanticUtilitySocket({
  utility,
  port,
  guidance,
  onPortActivate,
}: {
  utility: UtilityNode;
  port: UtilityNode["ports"][number];
  guidance?: PortPatchGuidance;
  onPortActivate: (portId: string) => void;
}) {
  const portId = utilityPortId(utility.id, port.id);
  return (
    <g
      aria-label={`${utility.displayName} ${port.label}, ${port.direction ?? "bidirectional"} ${port.signalType} signal socket`}
      className={`utility-socket${guidance ? ` patch-${guidance}` : ""}`}
      onClick={(event) => {
        event.stopPropagation();
        onPortActivate(portId);
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        event.stopPropagation();
        onPortActivate(portId);
      }}
      role="button"
      tabIndex={0}
      transform={`translate(${port.position.x} ${port.position.y})`}
    >
      <circle className="external-socket-hit" r="24" />
    </g>
  );
}

function PassiveMultCard({
  guidance,
  onNudge,
  onPointerDown,
  onPortActivate,
  selected,
  utility,
}: {
  guidance: ReadonlyMap<string, PortPatchGuidance>;
  onNudge: (dx: number, dy: number) => void;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
  onPortActivate: (portId: string) => void;
  selected: boolean;
  utility: UtilityNode;
}) {
  const height = utilityCardHeight(utility);
  const width = utilityCardWidth(utility);
  return (
    <g
      aria-label={`${utility.displayName}, ${utility.utilityType.replaceAll("-", " ")}`}
      className={`utility-node-card${selected ? " is-selected" : ""}`}
      data-utility-id={utility.id}
      onKeyDown={(event) => {
        const step = event.shiftKey ? 1 : 8;
        const delta =
          event.key === "ArrowLeft"
            ? [-step, 0]
            : event.key === "ArrowRight"
              ? [step, 0]
              : event.key === "ArrowUp"
                ? [0, -step]
                : event.key === "ArrowDown"
                  ? [0, step]
                  : null;
        if (!delta) return;
        event.preventDefault();
        onNudge(delta[0], delta[1]);
      }}
      onPointerDown={onPointerDown}
      role="group"
      tabIndex={0}
      transform={`translate(${utility.position.x} ${utility.position.y}) scale(${utility.scale})`}
    >
      <rect className="utility-node-face" height={height} width={width} />
      {utility.ports.map((port) => (
        <SemanticUtilitySocket
          guidance={guidance.get(utilityPortId(utility.id, port.id))}
          key={port.id}
          onPortActivate={onPortActivate}
          port={port}
          utility={utility}
        />
      ))}
    </g>
  );
}

function UtilityLevelKnob({
  label,
  onChange,
  onInteractionEnd,
  onInteractionStart,
  value,
}: {
  label: string;
  onChange: (value: number) => void;
  onInteractionEnd: () => void;
  onInteractionStart: () => void;
  value: number;
}) {
  const drag = useRef<{ pointerId: number; startValue: number; startY: number } | null>(null);
  const percent = Math.round(value * 100);
  return (
    <g
      aria-label={label}
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={percent}
      aria-valuetext={`${percent}%`}
      className="utility-control utility-level-control"
      onKeyDown={(event) => {
        const next =
          event.key === "Home"
            ? 0
            : event.key === "End"
              ? 1
              : event.key === "ArrowRight" || event.key === "ArrowUp"
                ? clamp(value + 0.05, 0, 1)
                : event.key === "ArrowLeft" || event.key === "ArrowDown"
                  ? clamp(value - 0.05, 0, 1)
                  : null;
        if (next === null) return;
        event.preventDefault();
        event.stopPropagation();
        onInteractionStart();
        onChange(next);
        onInteractionEnd();
      }}
      onPointerCancel={(event) => {
        if (drag.current?.pointerId !== event.pointerId) return;
        drag.current = null;
        onInteractionEnd();
      }}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = { pointerId: event.pointerId, startValue: value, startY: event.clientY };
        onInteractionStart();
      }}
      onPointerMove={(event) => {
        if (drag.current?.pointerId !== event.pointerId) return;
        event.preventDefault();
        event.stopPropagation();
        onChange(
          clamp(drag.current.startValue + (drag.current.startY - event.clientY) / 120, 0, 1),
        );
      }}
      onPointerUp={(event) => {
        if (drag.current?.pointerId !== event.pointerId) return;
        event.preventDefault();
        event.stopPropagation();
        drag.current = null;
        onInteractionEnd();
      }}
      role="slider"
      tabIndex={0}
    >
      <circle className="utility-knob-skirt" r="28" />
    </g>
  );
}

function AttenuatorCard({
  guidance,
  onControlChange,
  onInteractionEnd,
  onInteractionStart,
  onNudge,
  onPointerDown,
  onPortActivate,
  selected,
  utility,
}: {
  guidance: ReadonlyMap<string, PortPatchGuidance>;
  onControlChange: (controlId: string, value: number) => void;
  onInteractionEnd: () => void;
  onInteractionStart: (controlId: string) => void;
  onNudge: (dx: number, dy: number) => void;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
  onPortActivate: (portId: string) => void;
  selected: boolean;
  utility: UtilityNode;
}) {
  const width = utilityCardWidth(utility);
  const height = utilityCardHeight(utility);
  const control = utility.controls[0];
  return (
    <g
      aria-label={`${utility.displayName}, ${utility.utilityType.replaceAll("-", " ")}`}
      className={`utility-node-card utility-attenuator-card${selected ? " is-selected" : ""}`}
      data-utility-id={utility.id}
      onKeyDown={(event) => {
        const step = event.shiftKey ? 1 : 8;
        const delta =
          event.key === "ArrowLeft"
            ? [-step, 0]
            : event.key === "ArrowRight"
              ? [step, 0]
              : event.key === "ArrowUp"
                ? [0, -step]
                : event.key === "ArrowDown"
                  ? [0, step]
                  : null;
        if (!delta || (event.target as Element).closest(".utility-control")) return;
        event.preventDefault();
        onNudge(delta[0], delta[1]);
      }}
      onPointerDown={onPointerDown}
      role="group"
      tabIndex={0}
      transform={`translate(${utility.position.x} ${utility.position.y}) scale(${utility.scale})`}
    >
      <rect className="utility-node-face" height={height} width={width} />
      {utility.ports.map((port) => (
        <SemanticUtilitySocket
          guidance={guidance.get(utilityPortId(utility.id, port.id))}
          key={port.id}
          onPortActivate={onPortActivate}
          port={port}
          utility={utility}
        />
      ))}
      {control && (
        <g
          transform={`translate(${control.position?.x ?? width / 2} ${control.position?.y ?? height / 2})`}
        >
          <UtilityLevelKnob
            label={`${utility.displayName} ${control.label}`}
            onChange={(value) => onControlChange(control.id, value)}
            onInteractionEnd={onInteractionEnd}
            onInteractionStart={() => onInteractionStart(control.id)}
            value={control.value}
          />
        </g>
      )}
    </g>
  );
}

function ExternalEndpointCard({
  endpoint,
  guidance,
  selected,
  onActivate,
  onNudge,
  onPointerDown,
}: {
  endpoint: ExternalEndpointNode;
  guidance?: PortPatchGuidance;
  selected: boolean;
  onActivate: () => void;
  onNudge: (dx: number, dy: number) => void;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  return (
    <g
      aria-label={`${endpoint.displayName}, ${endpoint.direction} ${endpoint.signalType} endpoint`}
      className={`external-endpoint-card${selected ? " is-selected" : ""}${guidance ? ` patch-${guidance}` : ""}`}
      data-external-endpoint-id={endpoint.id}
      onClick={(event) => {
        event.stopPropagation();
        onActivate();
      }}
      onPointerDown={onPointerDown}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.stopPropagation();
          onActivate();
          return;
        }
        const step = event.shiftKey ? 1 : 8;
        const delta =
          event.key === "ArrowLeft"
            ? [-step, 0]
            : event.key === "ArrowRight"
              ? [step, 0]
              : event.key === "ArrowUp"
                ? [0, -step]
                : event.key === "ArrowDown"
                  ? [0, step]
                  : null;
        if (!delta) return;
        event.preventDefault();
        onNudge(delta[0], delta[1]);
      }}
      role="button"
      tabIndex={0}
      transform={`translate(${endpoint.position.x} ${endpoint.position.y})`}
    >
      <rect
        className="external-endpoint-face"
        height={EXTERNAL_CARD_HEIGHT}
        width={EXTERNAL_CARD_WIDTH}
      />
      <g className="external-endpoint-socket" transform={`translate(${EXTERNAL_CARD_WIDTH / 2} 0)`}>
        <circle className="external-socket-hit" r="24" />
      </g>
    </g>
  );
}

export function WorkspaceCanvas() {
  const canvasRef = useRef<SVGSVGElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const cameraLayerRef = useRef<SVGGElement>(null);
  const pixiRendererRef = useRef<PixiInstrumentRendererHandle>(null);
  const cameraFrameRef = useRef<number | null>(null);
  const cameraSettleFrameRef = useRef<number | null>(null);
  const wheelCommitRef = useRef<number | null>(null);
  const panRef = useRef<{ pointerId: number; x: number; y: number; camera: Camera } | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [camera, setCamera] = useState<Camera>({ x: 40, y: 40, scale: 0.75 });
  const cameraRef = useRef<Camera>(camera);
  const [pixiReady, setPixiReady] = useState(false);
  const [pixiBackend, setPixiBackend] = useState<PixiRendererBackend | null>(null);
  const [pixiError, setPixiError] = useState<string | null>(null);
  const [rendererAttempt, setRendererAttempt] = useState(0);
  const [pixiTargetId, setPixiTargetId] = useState<string | null>(null);
  const [pointerWorld, setPointerWorld] = useState<Point | null>(null);
  const cameraIsFitted = useRef(true);
  const previousFocusRevision = useRef(-1);
  const suppressEndpointClick = useRef<string | null>(null);
  const [confirmationPortId, setConfirmationPortId] = useState<string | null>(null);
  const [endpointEdit, setEndpointEdit] = useState<CableEndpointEdit | null>(null);
  const [looseEndDrag, setLooseEndDrag] = useState<LooseEndDrag | null>(null);
  const [junctionDrag, setJunctionDrag] = useState<JunctionDrag | null>(null);
  const [externalNodeDrag, setExternalNodeDrag] = useState<ExternalNodeDrag | null>(null);
  const [utilityNodeDrag, setUtilityNodeDrag] = useState<UtilityNodeDrag | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    position: Point;
    cableId: string | null;
  } | null>(null);

  const paintCamera = useCallback((next: Camera) => {
    cameraRef.current = next;
    if (cameraFrameRef.current !== null) return;
    cameraFrameRef.current = requestAnimationFrame(() => {
      cameraFrameRef.current = null;
      const pendingCamera = cameraRef.current;
      pixiRendererRef.current?.setCamera(pendingCamera);
      if (cameraLayerRef.current) {
        cameraLayerRef.current.style.transform = cameraTransform(pendingCamera);
      }
    });
  }, []);

  const commitCamera = useCallback((next: Camera) => {
    cameraRef.current = next;
    if (cameraFrameRef.current !== null) {
      cancelAnimationFrame(cameraFrameRef.current);
      cameraFrameRef.current = null;
    }
    pixiRendererRef.current?.setCamera(next);
    if (cameraLayerRef.current) {
      cameraLayerRef.current.style.transform = cameraTransform(next);
    }
    setCamera(next);
  }, []);

  const setCameraMoving = useCallback((moving: boolean, zooming = false) => {
    if (moving && cameraSettleFrameRef.current !== null) {
      cancelAnimationFrame(cameraSettleFrameRef.current);
      cameraSettleFrameRef.current = null;
    }
    if (moving && wheelCommitRef.current !== null) {
      window.clearTimeout(wheelCommitRef.current);
      wheelCommitRef.current = null;
    }
    workspaceRef.current?.classList.toggle("is-camera-moving", moving);
    workspaceRef.current?.classList.toggle("is-camera-zooming", moving && zooming);
  }, []);

  const settleCameraMotion = useCallback(() => {
    if (cameraSettleFrameRef.current !== null) {
      cancelAnimationFrame(cameraSettleFrameRef.current);
    }
    cameraSettleFrameRef.current = requestAnimationFrame(() => {
      cameraSettleFrameRef.current = null;
      setCameraMoving(false);
    });
  }, [setCameraMoving]);

  const scheduleCameraCommit = useCallback(
    (delay: number) => {
      if (wheelCommitRef.current !== null) {
        window.clearTimeout(wheelCommitRef.current);
      }
      wheelCommitRef.current = window.setTimeout(() => {
        wheelCommitRef.current = null;
        commitCamera(cameraRef.current);
        settleCameraMotion();
      }, delay);
    },
    [commitCamera, settleCameraMotion],
  );

  useEffect(
    () => () => {
      if (cameraFrameRef.current !== null) cancelAnimationFrame(cameraFrameRef.current);
      if (cameraSettleFrameRef.current !== null) {
        cancelAnimationFrame(cameraSettleFrameRef.current);
      }
      if (wheelCommitRef.current !== null) window.clearTimeout(wheelCommitRef.current);
    },
    [],
  );

  const {
    tool,
    viewMode,
    focusedSectionId,
    sectionFocusRevision,
    selectedId,
    pendingPortId,
    controlValues,
    cables,
    activeDocument,
    setControlValue,
    setUtilityControlValue,
    select,
    activatePort,
    updateCable,
    updateExternalEndpoint,
    updateUtility,
    finishLooseCable,
    deleteCable,
    cancelCable,
    focusSection,
    beginHistoryTransaction,
    endHistoryTransaction,
  } = useEditorStore();

  const endpointNodes = useMemo(() => externalEndpoints(activeDocument), [activeDocument]);
  const utilities = useMemo(() => utilityNodes(activeDocument), [activeDocument]);
  const instrumentBundle = useMemo(
    () => instrumentBundleForDocument(activeDocument),
    [activeDocument],
  );
  const instrumentRuntime = useMemo(
    () => cachedCompiledInstrumentRuntime(instrumentBundle.document),
    [instrumentBundle],
  );
  const instrumentDefinition = instrumentRuntime.definition;
  const worldWidth = instrumentDefinition.panel.width;
  const definition = useMemo(
    () => workspaceDefinition(activeDocument, instrumentDefinition),
    [activeDocument, instrumentDefinition],
  );
  const worldHeight = useMemo(
    () => workspaceWorldHeight(activeDocument, instrumentDefinition),
    [activeDocument, instrumentDefinition],
  );

  const portAnchors = useMemo(() => {
    const anchors = new Map(
      definition.ports.map((port) => [
        port.id,
        {
          ...port.position,
          surface: port.location === "rear" ? "rear" : "front",
        } satisfies CableAnchor,
      ]),
    );
    if (externalNodeDrag) {
      anchors.set("external:" + externalNodeDrag.endpointId, {
        x: externalNodeDrag.point.x + EXTERNAL_CARD_WIDTH / 2,
        y: externalNodeDrag.point.y,
        surface: "rear",
      });
    }
    if (utilityNodeDrag) {
      const utility = utilities.find((node) => node.id === utilityNodeDrag.endpointId);
      for (const port of utility?.ports ?? []) {
        anchors.set(utilityPortId(utilityNodeDrag.endpointId, port.id), {
          x: utilityNodeDrag.point.x + port.position.x * (utility?.scale ?? 1),
          y: utilityNodeDrag.point.y + port.position.y * (utility?.scale ?? 1),
          surface: "rear",
        });
      }
    }
    return anchors;
  }, [definition, externalNodeDrag, utilities, utilityNodeDrag]);

  const logicalAnchors = useMemo(
    () =>
      new Map<string, Point>([
        ...definition.ports.map((port) => [port.id, port.position] as const),
        ...definition.signalNodes.map((node) => [node.id, node.position] as const),
      ]),
    [definition],
  );
  const normalStates = useMemo(
    () => getNormalledRouteStates(definition, cables, controlValues),
    [cables, controlValues, definition],
  );
  const signalTrace = useMemo(
    () => traceSignal(definition, cables, selectedId, controlValues),
    [cables, controlValues, definition, selectedId],
  );
  const signalWarnings = useMemo(
    () => validateSignalPatch(definition, cables),
    [cables, definition],
  );

  const focusedSection = useMemo(
    () => instrumentDefinition.sections.find((section) => section.id === focusedSectionId) ?? null,
    [focusedSectionId, instrumentDefinition.sections],
  );
  const focusBounds = useMemo<Bounds>(() => {
    if (!focusedSectionId) return { x: 0, y: 0, width: worldWidth, height: worldHeight };
    const target =
      focusedSectionId === "rear-panel" ? instrumentDefinition.panel.rearBounds : focusedSection;
    if (!target) return { x: 0, y: 0, width: worldWidth, height: worldHeight };
    const margin = focusedSectionId === "rear-panel" ? 14 : 18;
    const x = Math.max(0, target.x - margin);
    const y = Math.max(0, target.y - margin);
    return {
      x,
      y,
      width: Math.min(worldWidth - x, target.width + margin * 2),
      height: Math.min(worldHeight - y, target.height + margin * 2),
    };
  }, [
    focusedSection,
    focusedSectionId,
    instrumentDefinition.panel.rearBounds,
    worldHeight,
    worldWidth,
  ]);
  const focusLabel =
    focusedSectionId === "rear-panel" ? "Rear I/O" : (focusedSection?.label ?? null);

  const resolveEndpoint = useCallback(
    (endpoint: CableEndpoint): CableAnchor | null => {
      if (endpoint.kind === "loose") {
        return { x: endpoint.x, y: endpoint.y, surface: "loose" };
      }
      return portAnchors.get(endpoint.portId) ?? null;
    },
    [portAnchors],
  );

  const fitCanvas = useCallback(() => {
    if (size.width <= 0 || size.height <= 0) return;
    const paddingX = Math.min(70, size.width * 0.06);
    const paddingY = Math.min(60, size.height * 0.1);
    const scale = clamp(
      Math.min(
        (size.width - paddingX * 2) / focusBounds.width,
        (size.height - paddingY * 2) / focusBounds.height,
      ),
      MIN_SCALE,
      MAX_SCALE,
    );
    cameraIsFitted.current = true;
    commitCamera({
      scale,
      x: (size.width - focusBounds.width * scale) / 2 - focusBounds.x * scale,
      y: (size.height - focusBounds.height * scale) / 2 - focusBounds.y * scale,
    });
  }, [commitCamera, focusBounds, size]);

  useLayoutEffect(() => {
    const element = canvasRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const focusChanged = previousFocusRevision.current !== sectionFocusRevision;
    if ((focusChanged || cameraIsFitted.current) && size.width > 200 && size.height > 200) {
      fitCanvas();
    }
    previousFocusRevision.current = sectionFocusRevision;
  }, [fitCanvas, sectionFocusRevision, size]);

  const cancelPendingCable = useCallback(() => {
    setConfirmationPortId(null);
    setEndpointEdit(null);
    setLooseEndDrag(null);
    cancelCable();
  }, [cancelCable]);

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") cancelPendingCable();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [cancelPendingCable]);

  const fitAllDevices = useCallback(() => {
    if (focusedSectionId) {
      focusSection(null);
      return;
    }
    fitCanvas();
  }, [fitCanvas, focusSection, focusedSectionId]);

  const resetView = useCallback(() => {
    focusSection(null);
    previousFocusRevision.current = useEditorStore.getState().sectionFocusRevision;
    cameraIsFitted.current = false;
    commitCamera({ x: 40, y: 40, scale: 0.75 });
  }, [commitCamera, focusSection]);

  const contextMenuSections: readonly ContextMenuSection[] = contextMenu
    ? [
        {
          id: "cable",
          label: contextMenu.cableId ? "Patch cable" : undefined,
          items: contextMenu.cableId
            ? [
                {
                  id: "remove-cable",
                  label: "Remove cable",
                  danger: true,
                  onSelect: () => deleteCable(contextMenu.cableId!),
                },
              ]
            : [],
        },
        {
          id: "view",
          items: [
            { id: "fit-all", label: "Fit all devices", onSelect: fitAllDevices },
            { id: "reset-view", label: "Reset view", onSelect: resetView },
          ],
        },
      ]
    : [];

  const clientToWorld = useCallback((clientX: number, clientY: number): Point => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const current = cameraRef.current;
    return {
      x: (clientX - rect.left - current.x) / current.scale,
      y: (clientY - rect.top - current.y) / current.scale,
    };
  }, []);

  const nudgeExternalNode = useCallback(
    (endpoint: ExternalEndpointNode, dx: number, dy: number) => {
      updateExternalEndpoint(endpoint.id, {
        position: {
          x: clamp(endpoint.position.x + dx, 0, worldWidth - EXTERNAL_CARD_WIDTH),
          y: Math.max(0, endpoint.position.y + dy),
        },
        edgeDock: "free",
      });
    },
    [updateExternalEndpoint, worldWidth],
  );

  const beginExternalNodeDrag = useCallback(
    (endpoint: ExternalEndpointNode, event: PointerEvent<SVGGElement>) => {
      if (tool !== "select" || event.button !== 0) return;
      if ((event.target as Element).closest(".external-endpoint-socket")) return;
      event.preventDefault();
      event.stopPropagation();
      cameraIsFitted.current = false;
      const pointer = clientToWorld(event.clientX, event.clientY);
      event.currentTarget.setPointerCapture(event.pointerId);
      setExternalNodeDrag({
        endpointId: endpoint.id,
        offset: {
          x: endpoint.position.x - pointer.x,
          y: endpoint.position.y - pointer.y,
        },
        pointerId: event.pointerId,
        point: endpoint.position,
        start: pointer,
        moved: false,
      });
      select(endpoint.id);
    },
    [clientToWorld, select, tool],
  );

  const nudgeUtilityNode = useCallback(
    (utility: UtilityNode, dx: number, dy: number) => {
      updateUtility(utility.id, {
        position: {
          x: clamp(utility.position.x + dx, 0, worldWidth - utilityCardWidth(utility)),
          y: Math.max(0, utility.position.y + dy),
        },
      });
    },
    [updateUtility, worldWidth],
  );

  const beginUtilityNodeDrag = useCallback(
    (utility: UtilityNode, event: PointerEvent<SVGGElement>) => {
      if (tool !== "select" || event.button !== 0) return;
      if ((event.target as Element).closest(".utility-socket, .utility-control")) return;
      event.preventDefault();
      event.stopPropagation();
      cameraIsFitted.current = false;
      const pointer = clientToWorld(event.clientX, event.clientY);
      event.currentTarget.setPointerCapture(event.pointerId);
      setUtilityNodeDrag({
        endpointId: utility.id,
        offset: {
          x: utility.position.x - pointer.x,
          y: utility.position.y - pointer.y,
        },
        pointerId: event.pointerId,
        point: utility.position,
        start: pointer,
        moved: false,
      });
      select(utility.id);
    },
    [clientToWorld, select, tool],
  );

  const zoomAt = useCallback(
    (nextScale: number, anchor = { x: size.width / 2, y: size.height / 2 }, transient = false) => {
      cameraIsFitted.current = false;
      const current = cameraRef.current;
      const scale = clamp(nextScale, MIN_SCALE, MAX_SCALE);
      const worldX = (anchor.x - current.x) / current.scale;
      const worldY = (anchor.y - current.y) / current.scale;
      const next = {
        scale,
        x: anchor.x - worldX * scale,
        y: anchor.y - worldY * scale,
      };
      if (transient) paintCamera(next);
      else commitCamera(next);
    },
    [commitCamera, paintCamera, size],
  );

  const handleWheel = (event: WheelEvent<SVGSVGElement>) => {
    event.preventDefault();
    setCameraMoving(true, true);
    const rect = event.currentTarget.getBoundingClientRect();
    const factor = Math.exp(-event.deltaY * 0.0012);
    zoomAt(
      cameraRef.current.scale * factor,
      {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      },
      true,
    );
    scheduleCameraCommit(120);
  };

  const beginPan = (event: PointerEvent<SVGSVGElement>) => {
    const canPan = event.button === 1 || (tool === "pan" && event.button === 0);
    if (!canPan) return;
    setCameraMoving(true);
    paintCamera(cameraRef.current);
    cameraIsFitted.current = false;
    event.currentTarget.setPointerCapture(event.pointerId);
    panRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      camera: cameraRef.current,
    };
  };

  const beginEndpointEdit = useCallback(
    (cableId: string, endpoint: CableEndpointRef) => {
      cancelCable();
      const cable = cables.find((candidate) => candidate.id === cableId);
      const targetEndpoint = cable ? cableEndpointAt(cable, endpoint) : null;
      const anchor = targetEndpoint ? resolveEndpoint(targetEndpoint) : null;
      if (anchor) setPointerWorld({ x: anchor.x, y: anchor.y });
      setConfirmationPortId(null);
      setLooseEndDrag(null);
      setEndpointEdit({ cableId, endpoint });
      select(cableId);
    },
    [cables, cancelCable, resolveEndpoint, select],
  );

  const beginJunctionDrag = useCallback(
    (cableId: string, event: PointerEvent<SVGGElement>) => {
      if (tool === "pan" || event.button !== 0) return;
      const cable = cables.find((candidate) => candidate.id === cableId);
      if (!cable?.junction) return;
      event.preventDefault();
      event.stopPropagation();
      const pointer = clientToWorld(event.clientX, event.clientY);
      event.currentTarget.setPointerCapture(event.pointerId);
      setJunctionDrag({
        cableId,
        offset: { x: cable.junction.x - pointer.x, y: cable.junction.y - pointer.y },
        pointerId: event.pointerId,
        point: cable.junction,
      });
      select(cableId);
    },
    [cables, clientToWorld, select, tool],
  );

  const nudgeCableJunction = useCallback(
    (cableId: string, dx: number, dy: number) => {
      const cable = cables.find((candidate) => candidate.id === cableId);
      if (!cable?.junction) return;
      updateCable(cableId, {
        junction: {
          x: clamp(cable.junction.x + dx, 0, worldWidth),
          y: Math.max(0, cable.junction.y + dy),
        },
      });
    },
    [cables, updateCable, worldWidth],
  );

  const nudgeLooseEnd = useCallback(
    (cableId: string, endpointKey: CableEndpointRef, dx: number, dy: number) => {
      const cable = cables.find((candidate) => candidate.id === cableId);
      const endpoint = cable ? cableEndpointAt(cable, endpointKey) : null;
      if (!cable || !endpoint || endpoint.kind !== "loose") return;
      const nextEndpoint = { ...endpoint, x: endpoint.x + dx, y: endpoint.y + dy };
      updateCable(cableId, cableEndpointUpdate(cable, endpointKey, nextEndpoint));
    },
    [cables, updateCable],
  );

  const beginLooseEndDrag = useCallback(
    (cableId: string, endpoint: CableEndpointRef, event: PointerEvent<SVGGElement>) => {
      if (tool === "pan") return;
      if (event.button !== 0) return;
      const cable = cables.find((candidate) => candidate.id === cableId);
      const looseEndpoint = cable ? cableEndpointAt(cable, endpoint) : null;
      if (!looseEndpoint || looseEndpoint.kind !== "loose") return;
      event.preventDefault();
      event.stopPropagation();
      const pointer = clientToWorld(event.clientX, event.clientY);
      event.currentTarget.setPointerCapture(event.pointerId);
      setConfirmationPortId(null);
      setEndpointEdit(null);
      setLooseEndDrag({
        cableId,
        endpoint,
        offset: { x: looseEndpoint.x - pointer.x, y: looseEndpoint.y - pointer.y },
        pointerId: event.pointerId,
        point: { x: looseEndpoint.x, y: looseEndpoint.y },
      });
      select(cableId);
    },
    [cables, clientToWorld, select, tool],
  );

  const movePointer = (event: PointerEvent<SVGSVGElement>) => {
    const panning = panRef.current?.pointerId === event.pointerId;
    if (panning && panRef.current) {
      paintCamera({
        ...panRef.current.camera,
        x: panRef.current.camera.x + event.clientX - panRef.current.x,
        y: panRef.current.camera.y + event.clientY - panRef.current.y,
      });
      return;
    }
    const point = clientToWorld(event.clientX, event.clientY);
    setPointerWorld(point);
    if (looseEndDrag?.pointerId === event.pointerId) {
      setLooseEndDrag({
        ...looseEndDrag,
        point: {
          x: point.x + looseEndDrag.offset.x,
          y: point.y + looseEndDrag.offset.y,
        },
      });
    }
    if (junctionDrag?.pointerId === event.pointerId) {
      setJunctionDrag({
        ...junctionDrag,
        point: {
          x: clamp(point.x + junctionDrag.offset.x, 0, worldWidth),
          y: Math.max(0, point.y + junctionDrag.offset.y),
        },
      });
    }
    if (utilityNodeDrag?.pointerId === event.pointerId) {
      const draggedUtility = utilities.find((utility) => utility.id === utilityNodeDrag.endpointId);
      const nextPoint = {
        x: clamp(
          point.x + utilityNodeDrag.offset.x,
          0,
          worldWidth - (draggedUtility ? utilityCardWidth(draggedUtility) : 220),
        ),
        y: Math.max(0, point.y + utilityNodeDrag.offset.y),
      };
      setUtilityNodeDrag({
        ...utilityNodeDrag,
        point: nextPoint,
        moved:
          utilityNodeDrag.moved ||
          Math.hypot(point.x - utilityNodeDrag.start.x, point.y - utilityNodeDrag.start.y) > 3,
      });
    }
    if (externalNodeDrag?.pointerId === event.pointerId) {
      const nextPoint = {
        x: clamp(point.x + externalNodeDrag.offset.x, 0, worldWidth - EXTERNAL_CARD_WIDTH),
        y: Math.max(0, point.y + externalNodeDrag.offset.y),
      };
      setExternalNodeDrag({
        ...externalNodeDrag,
        point: nextPoint,
        moved:
          externalNodeDrag.moved ||
          Math.hypot(point.x - externalNodeDrag.start.x, point.y - externalNodeDrag.start.y) > 3,
      });
    }
  };

  const findDropPort = useCallback(
    (point: Point) => {
      const hitRadius = clamp(26 / camera.scale, 20, 42);
      let nearest: PortDefinition | null = null;
      let nearestDistance = hitRadius;
      for (const port of definition.ports) {
        const distance = Math.hypot(port.position.x - point.x, port.position.y - point.y);
        if (distance > nearestDistance) continue;
        nearest = port;
        nearestDistance = distance;
      }
      return nearest;
    },
    [camera.scale, definition],
  );

  const finishPointer = (event: PointerEvent<SVGSVGElement>) => {
    if (panRef.current?.pointerId === event.pointerId) {
      panRef.current = null;
      scheduleCameraCommit(80);
      if (looseEndDrag?.pointerId === event.pointerId) setLooseEndDrag(null);
      return;
    }

    if (junctionDrag?.pointerId === event.pointerId) {
      updateCable(junctionDrag.cableId, { junction: junctionDrag.point });
      setJunctionDrag(null);
      return;
    }

    if (utilityNodeDrag?.pointerId === event.pointerId) {
      if (utilityNodeDrag.moved) {
        updateUtility(utilityNodeDrag.endpointId, { position: utilityNodeDrag.point });
      }
      setUtilityNodeDrag(null);
      return;
    }

    if (externalNodeDrag?.pointerId === event.pointerId) {
      if (externalNodeDrag.moved) {
        updateExternalEndpoint(externalNodeDrag.endpointId, {
          position: externalNodeDrag.point,
          edgeDock: "free",
        });
        suppressEndpointClick.current = externalNodeDrag.endpointId;
      }
      setExternalNodeDrag(null);
      return;
    }

    if (looseEndDrag?.pointerId === event.pointerId) {
      const edit = looseEndDrag;
      const cable = cables.find((candidate) => candidate.id === edit.cableId);
      const endpoint = cable ? cableEndpointAt(cable, edit.endpoint) : null;
      const fixedEndpoint = cable ? fixedEndpointFor(cable, edit.endpoint) : null;
      const dropPoint = clientToWorld(event.clientX, event.clientY);
      const targetPort = findDropPort(dropPoint);
      let handled = false;

      if (endpoint?.kind === "loose" && cable && fixedEndpoint && targetPort) {
        const analysis = analyzeCableDraft(
          definition,
          cables.filter((candidate) => candidate.id !== cable.id),
          fixedEndpoint,
          portEndpoint(targetPort.id),
        );
        if (analysis.status === "valid") {
          const nextEndpoint = portEndpoint(targetPort.id);
          updateCable(edit.cableId, cableEndpointUpdate(cable, edit.endpoint, nextEndpoint));
          handled = true;
        } else if (analysis.status === "warning") {
          setPointerWorld(targetPort.position);
          setEndpointEdit({ cableId: edit.cableId, endpoint: edit.endpoint });
          setConfirmationPortId(targetPort.id);
          handled = true;
        }
      }

      if (endpoint?.kind === "loose" && !handled) {
        const nextEndpoint = { ...endpoint, x: edit.point.x, y: edit.point.y };
        if (cable) {
          updateCable(edit.cableId, cableEndpointUpdate(cable, edit.endpoint, nextEndpoint));
        }
      }
      setLooseEndDrag(null);
    }
    if (panRef.current?.pointerId === event.pointerId) panRef.current = null;
  };

  const cancelPointer = (event: PointerEvent<SVGSVGElement>) => {
    if (utilityNodeDrag?.pointerId === event.pointerId) setUtilityNodeDrag(null);
    if (externalNodeDrag?.pointerId === event.pointerId) setExternalNodeDrag(null);
    if (looseEndDrag?.pointerId === event.pointerId) setLooseEndDrag(null);
    if (junctionDrag?.pointerId === event.pointerId) setJunctionDrag(null);
    if (panRef.current?.pointerId === event.pointerId) {
      panRef.current = null;
      scheduleCameraCommit(80);
    }
  };

  const activeCableEdit: CableEndpointEdit | null =
    endpointEdit ??
    (looseEndDrag ? { cableId: looseEndDrag.cableId, endpoint: looseEndDrag.endpoint } : null);

  const editingCable = activeCableEdit
    ? (cables.find((cable) => cable.id === activeCableEdit.cableId) ?? null)
    : null;
  const editFixedEndpoint =
    editingCable && activeCableEdit
      ? fixedEndpointFor(editingCable, activeCableEdit.endpoint)
      : null;
  const guidanceSource = activeCableEdit
    ? editFixedEndpoint
    : pendingPortId
      ? portEndpoint(pendingPortId)
      : null;
  const guidanceCables = activeCableEdit
    ? cables.filter((cable) => cable.id !== activeCableEdit.cableId)
    : cables;
  const guidanceSourcePortId = guidanceSource?.kind === "port" ? guidanceSource.portId : null;

  const portPatchGuidance = useMemo<ReadonlyMap<string, PortPatchGuidance>>(() => {
    const guidance = new Map<string, PortPatchGuidance>();
    if (!guidanceSource) return guidance;
    for (const port of definition.ports) {
      if (port.id === guidanceSourcePortId) {
        guidance.set(port.id, "source");
        continue;
      }
      const analysis = analyzeCableDraft(
        definition,
        guidanceCables,
        guidanceSource,
        portEndpoint(port.id),
      );
      const status: PortPatchGuidance =
        analysis.status === "valid"
          ? "compatible"
          : analysis.status === "warning"
            ? confirmationPortId === port.id
              ? "confirming"
              : "warning"
            : "invalid";
      guidance.set(port.id, status);
    }
    return guidance;
  }, [confirmationPortId, definition, guidanceCables, guidanceSource, guidanceSourcePortId]);

  const confirmationAnalysis = useMemo(
    () =>
      guidanceSource && confirmationPortId
        ? analyzeCableDraft(
            definition,
            guidanceCables,
            guidanceSource,
            portEndpoint(confirmationPortId),
          )
        : null,
    [confirmationPortId, definition, guidanceCables, guidanceSource],
  );
  const pendingPort = pendingPortId
    ? definition.ports.find((port) => port.id === pendingPortId)
    : null;
  const confirmationPort = confirmationPortId
    ? definition.ports.find((port) => port.id === confirmationPortId)
    : null;
  const guidanceSourceLabel = guidanceSourcePortId
    ? (definition.ports.find((port) => port.id === guidanceSourcePortId)?.label ?? "Source")
    : "Loose end";

  const handlePortActivate = useCallback(
    (portId: string) => {
      if (endpointEdit && editingCable && guidanceSource) {
        const analysis = analyzeCableDraft(
          definition,
          guidanceCables,
          guidanceSource,
          portEndpoint(portId),
        );
        if (analysis.status === "invalid" || analysis.status === "incomplete") {
          setConfirmationPortId(null);
          return;
        }
        if (analysis.status === "warning" && confirmationPortId !== portId) {
          setConfirmationPortId(portId);
          return;
        }
        const nextEndpoint = portEndpoint(portId);
        updateCable(
          endpointEdit.cableId,
          cableEndpointUpdate(editingCable, endpointEdit.endpoint, nextEndpoint),
        );
        setConfirmationPortId(null);
        setEndpointEdit(null);
        return;
      }

      if (tool !== "patch" || !pendingPortId || pendingPortId === portId) {
        setConfirmationPortId(null);
        activatePort(portId);
        return;
      }

      const analysis = analyzeCableDraft(
        definition,
        cables,
        portEndpoint(pendingPortId),
        portEndpoint(portId),
      );
      if (analysis.status === "invalid" || analysis.status === "incomplete") {
        setConfirmationPortId(null);
        return;
      }
      if (analysis.status === "warning" && confirmationPortId !== portId) {
        setConfirmationPortId(portId);
        select(portId);
        return;
      }

      setConfirmationPortId(null);
      activatePort(portId);
    },
    [
      activatePort,
      cables,
      confirmationPortId,
      definition,
      editingCable,
      endpointEdit,
      guidanceCables,
      guidanceSource,
      pendingPortId,
      select,
      tool,
      updateCable,
    ],
  );

  const pendingAnchor = pendingPortId ? portAnchors.get(pendingPortId) : null;
  const draftLooseAnchor: CableAnchor | null = pointerWorld
    ? { ...pointerWorld, surface: "loose" }
    : null;
  const draftRoute =
    pendingAnchor && draftLooseAnchor
      ? routeCable(pendingAnchor, draftLooseAnchor, cables.length + 1)
      : null;
  const orderedCables = useMemo(
    () =>
      selectedId
        ? [
            ...cables.filter((cable) => cable.id !== selectedId),
            ...cables.filter((cable) => cable.id === selectedId),
          ]
        : cables,
    [cables, selectedId],
  );
  const pixiCableVisuals = useMemo(
    () =>
      orderedCables
        .map((cable) => {
          const endpointPreview =
            looseEndDrag?.cableId === cable.id
              ? { endpoint: looseEndDrag.endpoint, point: looseEndDrag.point }
              : endpointEdit?.cableId === cable.id && pointerWorld
                ? { endpoint: endpointEdit.endpoint, point: pointerWorld }
                : undefined;
          const selected = selectedId === cable.id;
          const traced = signalTrace.cableIds.has(cable.id);
          const dimmed = viewMode === "flow" && signalTrace.active && !traced;
          const alpha = dimmed ? 0.18 : viewMode === "normalled" && !selected ? 0.45 : 1;
          return buildPixiCableVisual({
            alpha,
            cable,
            endpointPreview,
            junctionPreview: junctionDrag?.cableId === cable.id ? junctionDrag.point : undefined,
            resolveEndpoint,
            selected,
            traced,
          });
        })
        .filter((visual): visual is PixiCableVisual => visual !== null),
    [
      endpointEdit,
      junctionDrag,
      looseEndDrag,
      orderedCables,
      pointerWorld,
      resolveEndpoint,
      selectedId,
      signalTrace.active,
      signalTrace.cableIds,
      viewMode,
    ],
  );
  const pixiDraftCable = useMemo<PixiCableVisual | null>(() => {
    if (!pendingAnchor || !pointerWorld || !draftRoute) return null;
    return {
      alpha: 1,
      colour: "#f2d45c",
      endpoints: [
        {
          direction: { x: 0, y: 1 },
          loose: false,
          point: pendingAnchor,
          stackable: false,
        },
        {
          direction: draftRoute.looseDirectionB ?? { x: 0, y: 1 },
          loose: true,
          point: pointerWorld,
          stackable: false,
        },
      ],
      id: "__draft__",
      number: cables.length + 1,
      prepared: false,
      segments: [draftRoute.geometryPoints],
      selected: false,
      traced: false,
    };
  }, [cables.length, draftRoute, pendingAnchor, pointerWorld]);
  const allPixiCableVisuals = useMemo(
    () => (pixiDraftCable ? [...pixiCableVisuals, pixiDraftCable] : pixiCableVisuals),
    [pixiCableVisuals, pixiDraftCable],
  );

  const pixiExternalNodes = useMemo<PixiExternalNodeVisual[]>(
    () =>
      endpointNodes.map((endpoint) => {
        const displayEndpoint =
          externalNodeDrag?.endpointId === endpoint.id
            ? { ...endpoint, position: externalNodeDrag.point }
            : endpoint;
        const portId = "external:" + endpoint.id;
        return {
          endpoint: displayEndpoint,
          guidance: portPatchGuidance.get(portId),
          selected: selectedId === endpoint.id || selectedId === portId,
        };
      }),
    [endpointNodes, externalNodeDrag, portPatchGuidance, selectedId],
  );

  const pixiUtilityNodes = useMemo<PixiUtilityNodeVisual[]>(
    () =>
      utilities.map((utility) => {
        const displayUtility =
          utilityNodeDrag?.endpointId === utility.id
            ? { ...utility, position: utilityNodeDrag.point }
            : utility;
        return {
          guidance: Object.fromEntries(
            utility.ports.map((port) => {
              const portId = utilityPortId(utility.id, port.id);
              return [portId, portPatchGuidance.get(portId)];
            }),
          ),
          selected:
            selectedId === utility.id ||
            Boolean(selectedId?.startsWith("utility:" + utility.id + ":")),
          utility: displayUtility,
        };
      }),
    [portPatchGuidance, selectedId, utilities, utilityNodeDrag],
  );

  const pixiLogicalRoutes = useMemo<PixiLogicalRouteVisual[]>(() => {
    if (viewMode !== "normalled" && viewMode !== "flow") return [];
    const normalled = normalStates.flatMap((state, index) => {
      const from = logicalAnchors.get(state.route.fromId);
      const to = logicalAnchors.get(state.route.toId);
      if (!from || !to) return [];
      const traced = signalTrace.normalledRouteIds.has(state.route.id);
      return [
        {
          broken: state.status === "broken",
          dimmed: viewMode === "flow" && signalTrace.active && !traced,
          from,
          id: state.route.id,
          index,
          kind: "normalled" as const,
          label: state.route.label,
          selected: selectedId === state.route.id,
          signalType: state.route.signalType,
          to,
          traced,
        },
      ];
    });
    if (viewMode !== "flow") return normalled;
    return [
      ...normalled,
      ...definition.signalConnections.flatMap((connection, index) => {
        const from = logicalAnchors.get(connection.fromId);
        const to = logicalAnchors.get(connection.toId);
        if (!from || !to) return [];
        const traced = signalTrace.signalConnectionIds.has(connection.id);
        return [
          {
            broken: false,
            dimmed: signalTrace.active && !traced,
            from,
            id: connection.id,
            index: index + normalStates.length,
            kind: "internal" as const,
            label: connection.label,
            selected: selectedId === connection.id,
            signalType: connection.signalType,
            to,
            traced,
          },
        ];
      }),
    ];
  }, [
    definition.signalConnections,
    logicalAnchors,
    normalStates,
    selectedId,
    signalTrace.active,
    signalTrace.normalledRouteIds,
    signalTrace.signalConnectionIds,
    viewMode,
  ]);

  const pixiSignalNodes = useMemo<PixiSignalNodeVisual[]>(
    () =>
      viewMode === "normalled" || viewMode === "flow"
        ? definition.signalNodes.map((node) => {
            const traced = signalTrace.nodeIds.has(node.id);
            return {
              dimmed: viewMode === "flow" && signalTrace.active && !traced,
              id: node.id,
              label: node.shortLabel ?? node.label,
              position: node.position,
              signalType: node.signalType,
              traced,
            };
          })
        : [],
    [definition.signalNodes, signalTrace.active, signalTrace.nodeIds, viewMode],
  );

  const startControlInteraction = useCallback(
    (id: string) => beginHistoryTransaction(`Adjust ${id}`),
    [beginHistoryTransaction],
  );

  return (
    <div
      className={`workspace-canvas tool-${tool} view-${viewMode} renderer-pixi${pixiReady ? " pixi-ready" : ""}`}
      data-renderer-backend={
        pixiReady ? (pixiBackend ?? "unknown") : pixiError ? "unavailable" : "starting"
      }
      ref={workspaceRef}
    >
      <svg
        aria-label="Interactive instrument workspace"
        className="workspace-svg"
        onAuxClick={(event) => {
          if (event.button === 1) event.preventDefault();
        }}
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (pendingPortId || endpointEdit || looseEndDrag) {
            setContextMenu(null);
            cancelPendingCable();
            return;
          }
          const cableElement = (event.target as Element).closest<SVGElement>("[data-cable-id]");
          const cableId = cableElement?.dataset.cableId ?? null;
          if (cableId) select(cableId);
          setContextMenu({
            position: { x: event.clientX, y: event.clientY },
            cableId,
          });
        }}
        onPointerDown={beginPan}
        onPointerDownCapture={(event) => {
          if (event.button !== 1) return;
          event.preventDefault();
          event.stopPropagation();
          beginPan(event);
        }}
        onPointerMove={movePointer}
        onPointerCancel={cancelPointer}
        onPointerUp={finishPointer}
        onWheel={handleWheel}
        ref={canvasRef}
        role="application"
      >
        <defs>
          <pattern id="workspace-grid" height="28" patternUnits="userSpaceOnUse" width="28">
            <path
              d="M28 0H0v28"
              fill="none"
              stroke="currentColor"
              strokeOpacity=".1"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect
          className="workspace-background"
          height="100%"
          onClick={(event) => {
            if (endpointEdit) {
              const point = clientToWorld(event.clientX, event.clientY);
              const looseEndpoint: CableEndpoint = {
                kind: "loose",
                label: "Loose end",
                x: point.x,
                y: point.y,
              };
              const cable = cables.find((candidate) => candidate.id === endpointEdit.cableId);
              if (cable) {
                updateCable(
                  endpointEdit.cableId,
                  cableEndpointUpdate(cable, endpointEdit.endpoint, looseEndpoint),
                );
              }
              setConfirmationPortId(null);
              setEndpointEdit(null);
            } else if (pendingPortId) {
              const point = clientToWorld(event.clientX, event.clientY);
              setConfirmationPortId(null);
              finishLooseCable(point.x, point.y);
            } else if (tool === "select") {
              select(null);
            }
          }}
          width="100%"
        />
        <rect className="workspace-grid" fill="url(#workspace-grid)" height="100%" width="100%" />
        <g
          className="camera-layer"
          data-focused-section={focusedSectionId ?? "full-panel"}
          ref={cameraLayerRef}
          style={{ transform: cameraTransform(camera) }}
        >
          <InstrumentPanel
            controlValues={controlValues}
            definition={instrumentDefinition}
            onControlChange={setControlValue}
            onInteractionEnd={endHistoryTransaction}
            onInteractionStart={startControlInteraction}
            onPortActivate={handlePortActivate}
            onVisualTargetChange={setPixiTargetId}
            portPatchGuidance={portPatchGuidance}
            onSelect={select}
            selectedId={selectedId}
            traceActive={viewMode === "flow" && signalTrace.active}
            traceNodeIds={signalTrace.nodeIds}
          />
          {endpointNodes.map((endpoint) => {
            const portId = "external:" + endpoint.id;
            const displayEndpoint =
              externalNodeDrag?.endpointId === endpoint.id
                ? { ...endpoint, position: externalNodeDrag.point }
                : endpoint;
            return (
              <ExternalEndpointCard
                endpoint={displayEndpoint}
                guidance={portPatchGuidance.get(portId)}
                key={endpoint.id}
                onActivate={() => {
                  if (suppressEndpointClick.current === endpoint.id) {
                    suppressEndpointClick.current = null;
                    return;
                  }
                  handlePortActivate(portId);
                }}
                onNudge={(dx, dy) => nudgeExternalNode(endpoint, dx, dy)}
                onPointerDown={(event) => beginExternalNodeDrag(endpoint, event)}
                selected={selectedId === endpoint.id || selectedId === portId}
              />
            );
          })}
          {utilities.map((utility) => {
            const displayUtility =
              utilityNodeDrag?.endpointId === utility.id
                ? { ...utility, position: utilityNodeDrag.point }
                : utility;
            const commonProps = {
              guidance: portPatchGuidance,
              key: utility.id,
              onNudge: (dx: number, dy: number) => nudgeUtilityNode(utility, dx, dy),
              onPointerDown: (event: PointerEvent<SVGGElement>) =>
                beginUtilityNodeDrag(utility, event),
              onPortActivate: handlePortActivate,
              selected:
                selectedId === utility.id ||
                Boolean(selectedId?.startsWith("utility:" + utility.id + ":")),
              utility: displayUtility,
            };
            return utility.utilityType.includes("attenuator") ? (
              <AttenuatorCard
                {...commonProps}
                onControlChange={(controlId, value) =>
                  setUtilityControlValue(utility.id, controlId, value)
                }
                onInteractionEnd={endHistoryTransaction}
                onInteractionStart={(controlId) =>
                  beginHistoryTransaction(`Adjust ${utility.displayName} ${controlId}`)
                }
              />
            ) : (
              <PassiveMultCard {...commonProps} />
            );
          })}
          {(viewMode === "normalled" || viewMode === "flow") && (
            <g className="logical-route-layer">
              {normalStates.map((state, index) => {
                const from = logicalAnchors.get(state.route.fromId);
                const to = logicalAnchors.get(state.route.toId);
                if (!from || !to) return null;
                const traced = signalTrace.normalledRouteIds.has(state.route.id);
                return (
                  <LogicalRoute
                    broken={state.status === "broken"}
                    dimmed={viewMode === "flow" && signalTrace.active && !traced}
                    from={from}
                    id={state.route.id}
                    index={index}
                    key={state.route.id}
                    kind="normalled"
                    label={state.route.label}
                    onSelect={() => select(state.route.id)}
                    selected={selectedId === state.route.id}
                    signalType={state.route.signalType}
                    to={to}
                    traced={traced}
                  />
                );
              })}
              {viewMode === "flow" &&
                definition.signalConnections.map((connection, index) => {
                  const from = logicalAnchors.get(connection.fromId);
                  const to = logicalAnchors.get(connection.toId);
                  if (!from || !to) return null;
                  const traced = signalTrace.signalConnectionIds.has(connection.id);
                  return (
                    <LogicalRoute
                      dimmed={signalTrace.active && !traced}
                      from={from}
                      id={connection.id}
                      index={index + normalStates.length}
                      key={connection.id}
                      kind="internal"
                      label={connection.label}
                      onSelect={() => select(connection.id)}
                      selected={selectedId === connection.id}
                      signalType={connection.signalType}
                      to={to}
                      traced={traced}
                    />
                  );
                })}
            </g>
          )}
          <g className="cable-layer">
            {orderedCables.map((cable) => (
              <CablePath
                cable={cable}
                junctionPreview={
                  junctionDrag?.cableId === cable.id ? junctionDrag.point : undefined
                }
                endpointPreview={
                  looseEndDrag?.cableId === cable.id
                    ? { endpoint: looseEndDrag.endpoint, point: looseEndDrag.point }
                    : endpointEdit?.cableId === cable.id && pointerWorld
                      ? { endpoint: endpointEdit.endpoint, point: pointerWorld }
                      : undefined
                }
                reconnecting={endpointEdit?.cableId === cable.id}
                key={cable.id}
                onEndpointActivate={(endpoint) => beginEndpointEdit(cable.id, endpoint)}
                onLooseEndNudge={(endpoint, dx, dy) => nudgeLooseEnd(cable.id, endpoint, dx, dy)}
                onJunctionNudge={(dx, dy) => nudgeCableJunction(cable.id, dx, dy)}
                onJunctionPointerDown={(event) => beginJunctionDrag(cable.id, event)}
                onLooseEndPointerDown={(endpoint, event) =>
                  beginLooseEndDrag(cable.id, endpoint, event)
                }
                onSelect={() => select(cable.id)}
                resolveEndpoint={resolveEndpoint}
                selected={selectedId === cable.id}
                traced={signalTrace.cableIds.has(cable.id)}
                dimmed={
                  viewMode === "flow" && signalTrace.active && !signalTrace.cableIds.has(cable.id)
                }
              />
            ))}
          </g>
        </g>
      </svg>
      {contextMenu && (
        <ContextMenu
          ariaLabel="Patch workspace context menu"
          onClose={() => setContextMenu(null)}
          position={contextMenu.position}
          sections={contextMenuSections}
        />
      )}
      <Suspense fallback={null}>
        <PixiInstrumentRenderer
          camera={camera}
          cableVisuals={allPixiCableVisuals}
          controlValues={controlValues}
          definition={instrumentDefinition}
          instrumentRuntime={instrumentRuntime}
          externalNodes={pixiExternalNodes}
          hoveredId={pixiTargetId}
          logicalRoutes={pixiLogicalRoutes}
          key={rendererAttempt}
          onReady={(backend, fallbackReason) => {
            setPixiBackend(backend);
            setPixiError(fallbackReason ?? null);
            setPixiReady(true);
            pixiRendererRef.current?.setCamera(cameraRef.current);
          }}
          onUnavailable={(reason) => {
            setPixiBackend(null);
            setPixiError(reason);
            setPixiReady(false);
          }}
          portPatchGuidance={portPatchGuidance}
          ref={pixiRendererRef}
          selectedId={selectedId}
          traceActive={viewMode === "flow" && signalTrace.active}
          traceNodeIds={signalTrace.nodeIds}
          utilityNodes={pixiUtilityNodes}
          signalNodes={pixiSignalNodes}
        />
      </Suspense>

      {focusLabel && (
        <div className="section-focus-indicator" role="status">
          <span>
            <small>Focused section</small>
            <strong>{focusLabel}</strong>
          </span>
          <button onClick={() => focusSection(null)} type="button">
            <Icon name="fit" size={14} /> Full panel
          </button>
        </div>
      )}

      {viewMode === "normalled" && (
        <section className="route-status-panel" aria-label="Internal normal routes">
          <header>
            <div>
              <span>Internal normals</span>
              <strong>
                {normalStates.filter((state) => state.status === "active").length} active ·{" "}
                {normalStates.filter((state) => state.status === "broken").length} broken
              </strong>
            </div>
          </header>
          <div className="route-status-list">
            {normalStates.map((state) => (
              <button
                className={`is-${state.status}`}
                key={state.route.id}
                onClick={() => select(state.route.id)}
                type="button"
              >
                <i /> <span>{state.route.label}</span>
                <small>{state.status}</small>
              </button>
            ))}
          </div>
        </section>
      )}

      {viewMode === "flow" && (
        <section className="route-status-panel flow-status-panel" aria-label="Signal flow trace">
          <header>
            <div>
              <span>Signal flow</span>
              <strong>
                {signalTrace.active
                  ? `${signalTrace.upstreamNodeIds.size} upstream · ${signalTrace.downstreamNodeIds.size} downstream`
                  : "Select a jack, cable, or normal"}
              </strong>
            </div>
          </header>
          <p>
            {signalTrace.active
              ? "Bright paths are electrically connected to the selection. Dim paths are outside this trace."
              : "Choose any patch point or cable to trace its complete path through the instrument."}
          </p>
          <footer>
            {signalWarnings.length
              ? `${signalWarnings.filter((warning) => warning.severity === "warning").length} routing warnings · ${signalWarnings.filter((warning) => warning.severity === "info").length} notes`
              : "No routing warnings"}
          </footer>
        </section>
      )}

      {!pixiReady && (
        <section
          aria-live="polite"
          className={pixiError ? "renderer-gate is-error" : "renderer-gate is-loading"}
          role={pixiError ? "alert" : "status"}
        >
          <div>
            <span>Workspace renderer</span>
            <strong>
              {pixiError ? "Graphics renderer unavailable" : "Starting graphics renderer"}
            </strong>
            <p>
              {pixiError ??
                "Preparing the retained instrument scene. This normally takes only a moment."}
            </p>
            {pixiError && (
              <>
                <ul>
                  <li>Enable browser hardware acceleration.</li>
                  <li>Update the browser and graphics driver.</li>
                  <li>Reload after reconnecting a remote or virtual GPU session.</li>
                </ul>
                <button
                  onClick={() => {
                    setPixiBackend(null);
                    setPixiError(null);
                    setPixiReady(false);
                    setRendererAttempt((attempt) => attempt + 1);
                  }}
                  type="button"
                >
                  Retry renderer
                </button>
              </>
            )}
          </div>
        </section>
      )}

      {pixiReady && pixiBackend === "canvas" && pixiError && (
        <div className="renderer-notice is-warning" role="status">
          <span>
            <strong>Canvas fallback is active</strong>
            <small>{pixiError}</small>
          </span>
          <button
            aria-label="Dismiss renderer message"
            onClick={() => setPixiError(null)}
            type="button"
          >
            ×
          </button>
        </div>
      )}

      <div className="canvas-zoom-controls" aria-label="Workspace zoom controls">
        <button aria-label="Zoom out" onClick={() => zoomAt(camera.scale / 1.15)} type="button">
          <Icon name="zoomOut" size={16} />
        </button>
        <output aria-label="Current zoom">{Math.round(camera.scale * 100)}%</output>
        <button aria-label="Zoom in" onClick={() => zoomAt(camera.scale * 1.15)} type="button">
          <Icon name="zoomIn" size={16} />
        </button>
        <button
          aria-label={focusLabel ? `Fit ${focusLabel} section` : "Fit instrument"}
          onClick={fitCanvas}
          type="button"
        >
          <Icon name="fit" size={16} />
        </button>
      </div>

      {(pendingPortId || endpointEdit) && (
        <div className={`patching-hint${confirmationPort ? " is-warning" : ""}`} role="status">
          <span className="status-pulse" />
          <span>
            <strong>
              {confirmationPort
                ? `Review ${guidanceSourceLabel} → ${confirmationPort.label}`
                : endpointEdit && editingCable
                  ? `Reconnect Cable ${editingCable.number} ${cableEndpointName(endpointEdit.endpoint)}`
                  : `${pendingPort?.label ?? "Source"} connected`}
            </strong>
            <small>
              {confirmationPort
                ? confirmationAnalysis?.issues.find((issue) => issue.severity === "warning")
                    ?.message
                : endpointEdit
                  ? "Choose a highlighted jack, or click empty space to leave this end loose."
                  : "Compatible jacks are green; experimental routes are amber. Empty space creates a loose end."}
            </small>
          </span>
          {confirmationPort && <b>Click the amber jack again to confirm</b>}
          <kbd>Esc</kbd>
        </div>
      )}
    </div>
  );
}
